// Event foundation details, partner ticket offers and event invitations are stored inside
// existing text fields (event description, ticket tier description, booking notes) with
// markers, because no new database columns can be added right now.

export type EventKind = "popup" | "commissioned" | "closed_venue" | "outdoor";
export const EVENT_KINDS: { value: EventKind; label: string; help: string }[] = [
  { value: "popup", label: "Pop-up event", help: "A one-off event in a temporary space." },
  { value: "commissioned", label: "Commissioned work", help: "A private or paid booking for a client." },
  { value: "closed_venue", label: "Closed-off venue", help: "An indoor venue with controlled entry." },
  { value: "outdoor", label: "Outdoor venue", help: "Open-air grounds, parks or rooftops." },
];
export const MOODS = ["Chilled", "High energy", "Underground", "Luxury", "Family friendly", "Romantic", "Cultural", "Festival"];

export type EventFoundation = {
  kind: EventKind;
  atmosphere: string;
  mood: string;
  goals: string;
  gallery: string[];
  website?: string;
};

const EVENT_MARK = "[event]";
const PARTNER_MARK = "[partner]";
const SHARE_MARK = "[share]";
const INVITE_MARK = "[invite]";
const DEAL_MARK = "[deal]";

export const emptyFoundation = (): EventFoundation => ({ kind: "popup", atmosphere: "", mood: "", goals: "", gallery: [] });

/** Removes every hidden marker so only the human-written text is shown. */
export const cleanText = (text: string | null | undefined) => {
  if (!text) return "";
  let out = text;
  for (const m of [EVENT_MARK, PARTNER_MARK, SHARE_MARK, INVITE_MARK, DEAL_MARK]) {
    const i = out.indexOf(m);
    if (i >= 0) out = out.slice(0, i);
  }
  return out.trim();
};

const sliceJson = (text: string, mark: string) => {
  const i = text.indexOf(mark);
  if (i < 0) return null;
  let rest = text.slice(i + mark.length);
  for (const m of [EVENT_MARK, PARTNER_MARK, SHARE_MARK, INVITE_MARK, DEAL_MARK]) {
    const j = rest.indexOf(m);
    if (j >= 0) rest = rest.slice(0, j);
  }
  try { return JSON.parse(rest); } catch { return null; }
};

export const parseFoundation = (description: string | null | undefined): EventFoundation => {
  const raw = description ? sliceJson(description, EVENT_MARK) : null;
  const base = emptyFoundation();
  if (!raw || typeof raw !== "object") return base;
  return {
    kind: EVENT_KINDS.some((k) => k.value === raw.kind) ? raw.kind : base.kind,
    atmosphere: typeof raw.atmosphere === "string" ? raw.atmosphere : "",
    mood: typeof raw.mood === "string" ? raw.mood : "",
    goals: typeof raw.goals === "string" ? raw.goals : "",
    gallery: Array.isArray(raw.gallery) ? raw.gallery.filter((u: unknown) => typeof u === "string").slice(0, 10) : [],
    website: typeof raw.website === "string" ? raw.website : "",
  };
};

export const encodeFoundation = (text: string, f: EventFoundation) =>
  `${cleanText(text)}${EVENT_MARK}${JSON.stringify({ ...f, gallery: f.gallery.slice(0, 10) })}`;

export const kindLabel = (k: EventKind) => EVENT_KINDS.find((x) => x.value === k)?.label ?? "Event";

/* ---------- Partner offers on ticket tiers ---------- */

export type PartnerOffer = { discountPercent: number; sharePercent: number };

const clampPct = (n: number, max: number) => Math.min(max, Math.max(0, Math.round(Number(n) || 0)));

export const normalizeOffer = (o: PartnerOffer): PartnerOffer => ({
  discountPercent: clampPct(o.discountPercent, 90),
  sharePercent: clampPct(o.sharePercent, 50),
});

export const parsePartnerOffer = (description: string | null | undefined): PartnerOffer | null => {
  const raw = description ? sliceJson(description, PARTNER_MARK) : null;
  if (!raw) return null;
  const o = normalizeOffer({ discountPercent: raw.discountPercent, sharePercent: raw.sharePercent });
  return o.discountPercent || o.sharePercent ? o : null;
};

/** Builds the tier description: text + partner offer; the revenue share plan is appended after it. */
export const encodePartnerOffer = (text: string, offer: PartnerOffer) => {
  const o = normalizeOffer(offer);
  const base = cleanText(text);
  if (!o.discountPercent && !o.sharePercent) return base;
  return `${base}${PARTNER_MARK}${JSON.stringify(o)}`;
};

/** Price a creator or venue pays with the partner discount. */
export const partnerPrice = (price: number, offer: PartnerOffer | null) =>
  offer ? Math.round(price * (100 - normalizeOffer(offer).discountPercent)) / 100 : price;

/** What a partner earns for each ticket they help sell. */
export const partnerShareAmount = (price: number, offer: PartnerOffer | null) =>
  offer ? Math.round(price * normalizeOffer(offer).sharePercent) / 100 : 0;

/* ---------- Event invitations (stored on bookings) ---------- */

export type InviteMeta = {
  eventId: string;
  inviterId: string;
  role: "creator" | "venue";
  offer: number;
  overview: string;
  notes: string;
  moveLocation: boolean;
};

export const encodeInvite = (m: InviteMeta) =>
  `${INVITE_MARK}${JSON.stringify({ ...m, offer: Math.max(0, Number(m.offer) || 0), moveLocation: m.role === "venue" && m.moveLocation })}`;

export const parseInvite = (notes: string | null | undefined): InviteMeta | null => {
  const raw = notes ? sliceJson(notes, INVITE_MARK) : null;
  if (!raw || typeof raw.eventId !== "string" || typeof raw.inviterId !== "string") return null;
  return {
    eventId: raw.eventId,
    inviterId: raw.inviterId,
    role: raw.role === "venue" ? "venue" : "creator",
    offer: Math.max(0, Number(raw.offer) || 0),
    overview: String(raw.overview ?? ""),
    notes: String(raw.notes ?? ""),
    moveLocation: raw.role === "venue" && !!raw.moveLocation,
  };
};

/** Who sits on which side of the booking an invitation creates. */
export const invitePair = (args: { inviterId: string; inviteeId: string; role: "creator" | "venue"; eventVenueId: string | null }) =>
  args.role === "venue"
    ? { creator_id: args.inviterId, venue_id: args.inviteeId }
    : { creator_id: args.inviteeId, venue_id: args.eventVenueId && args.eventVenueId !== args.inviteeId ? args.eventVenueId : args.inviterId };

/** Builds the flyer prompt from the event foundation. */
export const flyerPrompt = (e: { title: string; date?: string; venue?: string; city?: string }, f: EventFoundation) =>
  [
    `Design a bold portrait event flyer for "${e.title}".`,
    `Event type: ${kindLabel(f.kind)}.`,
    f.mood && `Mood: ${f.mood}.`,
    f.atmosphere && `Atmosphere and setting: ${f.atmosphere}.`,
    e.date && `Show the date: ${e.date}.`,
    (e.venue || e.city) && `Location: ${[e.venue, e.city].filter(Boolean).join(", ")}.`,
    "Use clear, legible headline typography, nightlife lighting and a premium South African music-scene feel. No QR codes.",
  ].filter(Boolean).join(" ");
